import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DisplayGuestEmail, DisplayUserEmail, GalleryDto } from '@nusign/picflow-backend-types';
import { combineLatestWith, forkJoin, map, Observable, of, shareReplay, switchMap, take } from 'rxjs';
import { SwitchComponent } from '../../../components/switch/switch.component';
import { DialogOptions } from '../../../interfaces/dialog-data.interface';
import { unique } from '../../../lib/unique';
import { OnGetData } from '../../../services/dialog.service';
import { GalleryMemberService } from '../../../services/gallery-member.service';
import { GalleryService } from '../../../services/gallery.service';
import { TenantService } from '../../../services/tenant.service';
import { VisitorService } from '../../../services/visitor.service';
import { WatcherService, WatchingPerson } from '../../../services/watcher.service';
import { AvatarComponent } from '../../../shared/components/avatar/avatar.component';
import { ContextMenuComponent } from '../../../shared/components/context-menu/context-menu.component';
import { LoadingIndicatorComponent } from '../../../shared/components/loading-indicator/loading-indicator.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { ClickTrackDirective } from '../../../shared/directives/click-track.directive';
import { TooltipDirective } from '../../../shared/directives/tooltip.directive';
import { ProfilePreviewComponent } from '../../components/profile-preview/profile-preview.component';

@Component({
  selector: 'app-gallery-subscribers-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SvgComponent,
    AsyncPipe,
    LoadingIndicatorComponent,
    ButtonDirective,
    TooltipDirective,
    SwitchComponent,
    FormsModule,
    ClickTrackDirective,
    AvatarComponent,
    ContextMenuComponent,
    ProfilePreviewComponent,
  ],
  templateUrl: './gallery-subscribers-dialog.component.html',
  styleUrl: './gallery-subscribers-dialog.component.scss',
})
export class GallerySubscribersDialogComponent implements OnGetData {
  dialogName = 'gallery-subscribers';
  private watcherService = inject(WatcherService);
  private visitorService = inject(VisitorService);
  private tenantService = inject(TenantService);
  private galleryMemberService = inject(GalleryMemberService);
  private galleryService = inject(GalleryService);
  close: () => void;
  gallery: GalleryDto;

  idEmailMap$ = of(undefined).pipe(
    switchMap(() =>
      forkJoin([
        this.visitorService.getGalleryVisitors(this.gallery.id).pipe(take(1)),
        this.galleryMemberService.getAllGalleryMembers(true).pipe(take(1)),
        this.tenantService.getMembers().pipe(take(1)),
      ])
    ),
    map(([visitors, galleryMembers, members]) => {
      const idEmailMap = {};
      const allUsers = [
        ...visitors.map(v => (v.user as DisplayUserEmail) || (v.guest as DisplayGuestEmail)),
        ...galleryMembers.map(g => g.user as DisplayUserEmail),
        ...members,
      ];
      allUsers.forEach(user => (idEmailMap[user.id] = user.email));
      return idEmailMap;
    }),
    shareReplay(1)
  );

  galleryWatchers$: Observable<(WatchingPerson & { email: string })[]>;
  galleryAssetWatchers$: Observable<(WatchingPerson & { email: string })[]>;

  dialogOptions(): DialogOptions {
    return {};
  }

  onGetData(data: { gallery: GalleryDto }, close: () => void) {
    this.gallery = data.gallery;
    this.close = close;

    this.galleryWatchers$ = this.watcherService.getGalleryWatchers(this.gallery.id).pipe(
      combineLatestWith(this.idEmailMap$),
      map(([watchers, idEmailMap]) =>
        watchers.map(watcher => {
          return { ...watcher, email: idEmailMap[watcher.id] };
        })
      )
    );
    this.galleryAssetWatchers$ = this.watcherService.getGalleryWatchers(this.gallery.id, 'asset').pipe(
      combineLatestWith(this.idEmailMap$),
      map(([watchers, idEmailMap]) =>
        unique(
          watchers.map(watcher => {
            return { ...watcher, email: idEmailMap[watcher.id] };
          }),
          'id'
        )
      )
    );
  }

  removeWatcher(id: string) {
    this.galleryWatchers$.pipe(take(1)).subscribe(watchers => {
      const watcher = watchers.find(w => w.id === id);
      if (watcher) this.watcherService.removeWatcher$.next(watcher.watcher_id);
    });
    this.galleryAssetWatchers$.pipe(take(1)).subscribe(watchers => {
      watchers
        .filter(w => w.id === id)
        .forEach(watcher => {
          this.watcherService.removeWatcher$.next(watcher.watcher_id);
        });
    });
  }

  toggleNotificationsEnabled() {
    this.galleryService.updateGallery(this.gallery.id, { notifications_enabled: this.gallery.notifications_enabled });
  }
}
